import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, vShow as _vShow, withModifiers as _withModifiers, withKeys as _withKeys, Fragment as _Fragment, normalizeClass as _normalizeClass, renderList as _renderList, renderSlot as _renderSlot, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-8cb149aa"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "select__label",
  "data-ts": "select-label"
}
const _hoisted_2 = ["aria-label", "onKeydown"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "select__search-icon bx bx-search" }, null, -1))
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = ["onKeydown"]
const _hoisted_6 = ["aria-expanded"]
const _hoisted_7 = ["data-ts", "onClick"]
const _hoisted_8 = {
  key: 1,
  class: "select__error",
  "data-ts": "error-label"
}

import { ref, watchEffect, watch, nextTick } from 'vue';

type Option = {
	label: string;
	value: string | number;
} | null;


export default /*#__PURE__*/_defineComponent({
  __name: 'SelectBox',
  props: {
    placeholder: {},
    label: {},
    options: {},
    selectedValue: {},
    disabled: { type: Boolean },
    hasError: { type: Boolean },
    errorLabel: {},
    ariaLabelOpen: {},
    ariaLabelClose: {},
    dropdownPositionTop: { type: Boolean },
    hasSearch: { type: Boolean }
  },
  emits: ['select', 'clearSearchTerm', 'inputSearchTerm'],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

const isOpen = ref(false);
const selectedOption = ref<Option>(null);
const arrowCounter = ref(-1);
const optionRefs = ref([]);
const searchTerm = ref('');
const searchAutocompleteInput = ref(); /** Focus input when clicking clear btn */

watchEffect(() => {
	if (props.options?.length && props.selectedValue) {
		selectedOption.value = props.options.find(
			(option) => option?.value === props.selectedValue
		);
	} else {
		selectedOption.value = null;
	}
});

const closeDropdown = () => {
	isOpen.value = false;
	arrowCounter.value = -1; /** Keyboard navigation */
};

function handleSelect(option: Option) {
	/** placeholders and noMatch options are passed
	 * with an empty string as value */
	if (option?.value) {
		selectedOption.value = option;
		searchTerm.value = option.label;
		emit('select', option.value);
		if (props.hasSearch) emit('inputSearchTerm', option.label);
	}

	closeDropdown();
}

const handleClear = () => {
	isOpen.value = true;
	searchTerm.value = '';
	searchAutocompleteInput.value.focus();
	emit('clearSearchTerm');
};

const isItemSelected = (option): boolean => option.value === selectedOption.value?.value;

const scrollInView = () => {
	const activeElement = optionRefs.value.find(
		(optionRef) => optionRefs.value.indexOf(optionRef) === arrowCounter.value
	);

	if (activeElement) {
		activeElement?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		});
	}
};

const handleArrowDown = () => {
	if (arrowCounter.value + 1 < props.options.length) {
		arrowCounter.value += 1;
	}
	scrollInView();
};

const handleArrowUp = () => {
	if (arrowCounter.value > 0) {
		arrowCounter.value -= 1;
	}
	scrollInView();
};

const handleKeydownEnter = () => {
	if (isOpen.value && arrowCounter.value !== -1) {
		handleSelect(props.options[arrowCounter.value]);
		closeDropdown();
	} else {
		isOpen.value = !(isOpen.value && arrowCounter.value === -1);
	}
};

const handleInput = (e) => {
	if (!isOpen.value) isOpen.value = true;

	if (!e.target.value) {
		emit('clearSearchTerm');
	} else {
		emit('inputSearchTerm', e.target.value);
	}
};

const clickOutside = () => {
	closeDropdown();

	if (props.hasSearch && selectedOption.value) {
		searchTerm.value = `${selectedOption.value?.label}`;
	}
};

watch(isOpen, async () => {
	await nextTick();
	if (isOpen.value && selectedOption.value?.label) {
		const selectedElement = optionRefs.value.find((optionRef) => {
			return optionRef.innerText === `${selectedOption.value?.label}`;
		});
		selectedElement.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		});
	}
});

return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
			'select',
			{
				'select--disabled': _ctx.disabled,
			},
		]),
    "data-ts": "select-box"
  }, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(_ctx.label), 1))
      : _createCommentVNode("", true),
    _withDirectives((_openBlock(), _createElementBlock("div", {
      "aria-label": isOpen.value ? _ctx.ariaLabelClose : _ctx.ariaLabelOpen,
      "aria-haspopup": "listbox",
      tabindex: "0",
      class: _normalizeClass(['select__display-wrapper', { 'select__display-wrapper--error': _ctx.hasError }]),
      "data-ts": "select",
      onClick: _cache[1] || (_cache[1] = ($event: any) => (isOpen.value = !isOpen.value)),
      onKeydown: [
        _withKeys(_withModifiers(handleKeydownEnter, ["prevent"]), ["enter"]),
        _withKeys(_withModifiers(handleArrowDown, ["prevent"]), ["down"]),
        _withKeys(_withModifiers(handleArrowUp, ["prevent"]), ["up"]),
        _cache[2] || (_cache[2] = _withKeys(($event: any) => (isOpen.value = false), ["esc"]))
      ]
    }, [
      (_ctx.hasSearch)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("label", null, [
              _hoisted_3,
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
                ref_key: "searchAutocompleteInput",
                ref: searchAutocompleteInput,
                "data-ts": "search-autocomplete-input",
                placeholder: _ctx.placeholder,
                type: "text",
                class: "select__input",
                onInput: handleInput
              }, null, 40, _hoisted_4), [
                [_vModelText, searchTerm.value]
              ])
            ]),
            _withDirectives(_createElementVNode("button", {
              type: "button",
              tabindex: "0",
              "data-ts": "autocomplete-clear-btn",
              "aria-label": "Clear input",
              class: "select__clear-btn bx bx-x",
              onClick: _withModifiers(handleClear, ["stop"]),
              onKeydown: _withKeys(_withModifiers(handleClear, ["stop"]), ["enter"])
            }, null, 40, _hoisted_5), [
              [_vShow, searchTerm.value]
            ])
          ], 64))
        : (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass([
					'select__selected',
					{
						select__selected__placeholder: !selectedOption.value,
					},
				])
          }, [
            _createElementVNode("span", null, _toDisplayString(selectedOption.value ? selectedOption.value?.label : _ctx.placeholder), 1),
            _createElementVNode("span", {
              class: _normalizeClass([
						'select__selected__arrow bx bx-chevron-down',
						{ 'select__selected__arrow--open': isOpen.value },
					])
            }, null, 2)
          ], 2)),
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("ul", {
            key: 2,
            id: "options",
            class: _normalizeClass([
					'select__items',
					'popover-container',
					{ 'select__items--top': _ctx.dropdownPositionTop },
				]),
            role: "list",
            "aria-expanded": `${isOpen.value}`
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option, i) => {
              return (_openBlock(), _createElementBlock("li", {
                key: `select-option-${i}`,
                ref_for: true,
                ref_key: "optionRefs",
                ref: optionRefs,
                "data-ts": `select-option-${i}`,
                class: _normalizeClass([
						'select__items__item',
						{ 'select__items__item--disabled': !option.value },
						{ 'select__items__item--active': i === arrowCounter.value && option.value },
						{ 'select__items__item--selected': isItemSelected(option) },
					]),
                role: "option",
                onClick: _withModifiers(($event: any) => (handleSelect(option)), ["stop"])
              }, [
                _renderSlot(_ctx.$slots, "label", { option: option }, () => [
                  _createTextVNode(_toDisplayString(option.label), 1)
                ])
              ], 10, _hoisted_7))
            }), 128))
          ], 10, _hoisted_6))
        : _createCommentVNode("", true)
    ], 42, _hoisted_2)), [
      [_directive_click_outside, clickOutside]
    ]),
    (_ctx.hasError)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(_ctx.errorLabel), 1))
      : _createCommentVNode("", true)
  ], 2))
}
}

})